import { useRef } from 'react';
import { createSearchParams, useSearchParams as useRouterSearchParams } from 'react-router';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';
/**
 * `useSearchParams` whose updates keep composing when several of them are fired within one tick.
 *
 * react-router v6 handed every functional updater the very same mutable `URLSearchParams` instance,
 * so a second `setSearchParams` call in the same tick saw whatever the first one had written. v7
 * hands out a fresh copy per call, which makes the second call silently drop the first one's
 * changes. Filter hooks persist several params from a single effect and rely on the composing
 * behaviour, so feed the value produced by the previous call back in as the base.
 *
 * The batch lives exactly as long as v6's shared instance did: until the URL search string — and
 * with it the identity of `searchParams` — actually changes.
 */
export const useSearchParams = (defaultInit) => {
    const [searchParams, setRouterSearchParams] = useRouterSearchParams(defaultInit);
    const batchRef = useRef(null);
    const setSearchParams = useCallbackRef((nextInit, navigateOptions) => {
        setRouterSearchParams((currentParams) => {
            const batch = batchRef.current;
            const baseParams = batch?.basedOn === searchParams ? batch.params : currentParams;
            const nextParams = createSearchParams(typeof nextInit === 'function' ? nextInit(baseParams) : nextInit);
            batchRef.current = { basedOn: searchParams, params: nextParams };
            return nextParams;
        }, navigateOptions);
    });
    return [searchParams, setSearchParams];
};
